import { Link } from "react-router-dom";
import PageMeta from "@/components/seo/PageMeta";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import SectionTitle from "@/components/base/SectionTitle";
import {
  contentPrinciples,
  SITE_URL,
  siteInfo,
  siteStats,
  testimonials,
} from "@/mocks/site";

const milestones = [
  {
    year: "2021",
    title: "从零散片段开始",
    desc: "最初只是把随手看到的现场画面记在同一个文档里，慢慢发现零散内容很难回看。",
  },
  {
    year: "2023",
    title: "分类体系成型",
    desc: "确定花絮、路透、侧拍、彩蛋、候场五个分类，让每条素材都有明确的归属。",
  },
  {
    year: "2025",
    title: "从片段到合集",
    desc: "不再单条罗列，而是把同一场景的多个片段整理成一篇可连续阅读的合集。",
  },
  {
    year: "2026",
    title: "持续按周补充",
    desc: "新素材出现后补进对应分类的合集里，旧合集也会在补充内容后更新正文。",
  },
];

export default function About() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 | 吃瓜片场",
    url: `${SITE_URL}/about`,
    inLanguage: "zh-CN",
    description:
      "了解吃瓜片场的品牌定位、内容原则与发展理念，我们围绕私拍吃瓜相关花絮、路透、侧拍、彩蛋与候场片段进行整理。",
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteInfo.name,
    url: SITE_URL,
    email: siteInfo.email,
    foundingDate: String(siteInfo.startYear),
    description:
      "吃瓜片场是围绕影视剧组花絮、路透、侧拍、彩蛋与候场片段的中文合集站，按分类整理正片之外的拍摄内容。",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "关于我们",
        item: `${SITE_URL}/about`,
      },
    ],
  };

  return (
    <>
      <PageMeta
        title="关于我们 | 吃瓜片场私拍吃瓜合集站"
        description="吃瓜片场专注整理私拍吃瓜花絮、路透、侧拍、彩蛋与候场片段，坚持分类先行、只依据公开画面描述，并持续按周补充新的现场内容。"
        keywords="关于吃瓜片场,私拍吃瓜合集站,内容原则"
        path="/about"
        jsonLd={[aboutSchema, organizationSchema, breadcrumbSchema]}
      />

      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <Breadcrumbs items={[{ label: "关于我们" }]} />
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-5">
            关于吃瓜片场
          </h1>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mt-4 max-w-3xl">
            我们做的是同一件事：把剧组里零散流出的花絮、路透、侧拍、彩蛋与候场片段，
            整理成有分类、有时间线、能顺着读下去的合集。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20" aria-labelledby="about-brand">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs tracking-[0.3em] text-primary-600 mb-3">BRAND</p>
            <h2
              id="about-brand"
              className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-snug"
            >
              网站定位：正片之外的细节整理者
            </h2>
            <p className="text-base text-foreground-700 leading-loose mt-5">
              {siteInfo.intro}
            </p>
            <p className="text-base text-foreground-700 leading-loose mt-4">
              我们不追热点榜单，也不做主观评价。判断一条素材是否值得整理的标准很简单：
              它能否帮助读者理解一个画面是怎么被拍出来的，或者一个细节为什么会出现。
              能回答这两个问题的内容，才会被收进合集。
            </p>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-9">
              {siteStats.map((stat) => (
                <div key={stat.label}>
                  <dt className="font-heading text-2xl font-bold text-primary-700">
                    {stat.value}
                  </dt>
                  <dd className="text-xs text-foreground-600 mt-1.5 leading-relaxed">
                    {stat.label}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="lg:col-span-5">
            <div className="w-full h-72 md:h-[420px] rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20behind%20the%20scenes%20studio%20corner%20with%20a%20vintage%20camera%2C%20rolled%20storyboards%20and%20a%20single%20amber%20desk%20lamp%2C%20clean%20minimal%20background%2C%20analog%20film%20grain%2C%20editorial%20still%20life%20photography%20in%20warm%20neutral%20tones&width=900&height=1100&seq=chigua-about-01&orientation=portrait"
                alt="暖色调片场角落中的相机与分镜稿"
                title="吃瓜片场 内容整理"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section
        className="w-full bg-background-100 border-y border-background-200"
        aria-labelledby="about-principles"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
          <SectionTitle
            eyebrow="PRINCIPLES"
            title="内容原则：四条我们不会跳过"
            desc="这些原则决定了哪些内容会被整理，哪些会被排除在外。"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {contentPrinciples.map((item) => (
              <div
                key={item.title}
                className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6"
              >
                <span className="w-11 h-11 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center text-lg">
                  <i className={item.icon} aria-hidden="true" />
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-950 mt-4">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-foreground-600 mt-2">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20" aria-labelledby="about-milestones">
        <div className="max-w-7xl mx-auto">
          <SectionTitle
            eyebrow="MILESTONES"
            title="发展理念：一步步把碎片拼回来"
            desc="从最早的随手记录，到现在的分类合集，整理方式一直在调整。"
          />
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {milestones.map((item, index) => (
              <li
                key={item.year}
                className="relative bg-background-50 border border-background-200 rounded-lg p-5 md:p-6"
              >
                <span className="font-heading text-3xl font-black text-background-300">
                  {item.year}
                </span>
                <span className="absolute top-6 right-5 text-xs text-foreground-400">
                  0{index + 1}
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-950 mt-3">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-foreground-600 mt-2">
                  {item.desc}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section
        className="w-full bg-background-100 border-y border-background-200"
        aria-labelledby="about-voice"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
          <SectionTitle eyebrow="VOICES" title="读者反馈" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {testimonials.slice(0, 2).map((item) => (
              <figure
                key={item.name}
                className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-7"
              >
                <i className="ri-double-quotes-l text-2xl text-primary-400" aria-hidden="true" />
                <blockquote className="text-base leading-relaxed text-foreground-700 mt-3">
                  {item.content}
                </blockquote>
                <figcaption className="flex items-center gap-3 mt-5">
                  <span className="w-11 h-11 rounded-full overflow-hidden bg-background-200 shrink-0">
                    <img
                      src={item.avatar}
                      alt={`${item.name} 头像`}
                      title={item.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-foreground-900">
                      {item.name}
                    </span>
                    <span className="text-xs text-foreground-500 mt-0.5">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20" aria-labelledby="about-contact">
        <div className="max-w-7xl mx-auto bg-secondary-950 text-secondary-100 rounded-lg px-6 md:px-12 py-12 md:py-16 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-14">
          <div className="lg:flex-1">
            <h2
              id="about-contact"
              className="font-heading text-2xl md:text-3xl font-bold text-background-50 leading-snug"
            >
              想聊聊内容，或提供一条线索？
            </h2>
            <p className="text-sm md:text-base text-secondary-200/90 leading-relaxed mt-4 max-w-xl">
              我们更希望听到具体的描述：哪部作品、哪个场景、大致的时间点。
              越具体，越容易核对，也越可能被整理成新的合集。
            </p>
          </div>
          <div className="flex flex-col gap-4 lg:shrink-0 lg:min-w-[280px]">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-md bg-secondary-900 flex items-center justify-center text-accent-400">
                <i className="ri-mail-line" aria-hidden="true" />
              </span>
              <span className="text-sm text-secondary-200">{siteInfo.email}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-md bg-secondary-900 flex items-center justify-center text-accent-400">
                <i className="ri-wechat-line" aria-hidden="true" />
              </span>
              <span className="text-sm text-secondary-200">{siteInfo.wechat}</span>
            </div>
            <Link
              to="/contact"
              className="mt-2 px-6 py-3.5 rounded-md bg-accent-500 text-secondary-950 text-sm font-medium hover:bg-accent-400 transition-colors text-center whitespace-nowrap"
            >
              前往联系我们
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}